import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ttProtectionDefaultValues } from "./ttProtectionDefaultValues";
import { TTProtection } from "../TTProtection";
import PageHeader from "../../../Layout/Header/PageHeader";
import PageHeaderButton from "../../../Layout/Header/PageHeaderButton";
import { MdmTabs } from "../../../Layout/Tabs/MdmTabs";
import { TTProtectionForm } from "./Information/TTProtectionForm";
import { INFORMATION } from "../../../Layout/Tabs/MdmTabPanelConstants";
import { ttProtectionSchema } from "./ttProtectionSchema";
import { isFormSubmitForbidden } from "../../../Forms/isFormSubmitForbidden";
import { AffectedSprsForm } from "./AffectedSprs/AffectedSprsForm";
import { useIsUserInRoles } from "../../../Auth/useIsUserInRoles";
import { AuthorizationPolicies } from "../../../Auth/AuthorizationPolicy";

type Props = {
  ttProtectionId?: number | null;
  title: string;
  ttProtection: TTProtection | undefined;
  onSaveAsNewVersion: (ttProtection: TTProtection) => void;
  onSaveWithoutVersion?: (ttProtection: TTProtection) => void;
  disabledFields?: Array<string> | undefined;
};

export const TTProtectionFormTabs = ({
  title,
  ttProtection,
  onSaveAsNewVersion,
  onSaveWithoutVersion,
  disabledFields,
}: Props) => {
  const isUserAdminOrRiskManager = useIsUserInRoles(
    AuthorizationPolicies.RequireAdminRiskmanagerRoles
  );

  const methods = useForm<TTProtection>({
    resolver: yupResolver(ttProtectionSchema),
    defaultValues: ttProtectionDefaultValues,
    mode: "all",
    disabled: !isUserAdminOrRiskManager,
  });

  useEffect(() => {
    methods.reset(ttProtection);
    return () => methods.reset(ttProtectionDefaultValues);
  }, [ttProtection, methods, methods.reset]);

  return (
    <>
      <PageHeader headerText={title}>
        {onSaveWithoutVersion && ttProtection?.Id !== 0 && (
          <PageHeaderButton
            onClick={methods.handleSubmit((values: TTProtection) => onSaveWithoutVersion(values))}
            disabled={isFormSubmitForbidden(methods.formState)}
          >
            Save
          </PageHeaderButton>
        )}
        <PageHeaderButton
          onClick={methods.handleSubmit((values: TTProtection) => onSaveAsNewVersion(values))}
          disabled={isFormSubmitForbidden(methods.formState)}
        >
          Save as new Version
        </PageHeaderButton>
      </PageHeader>

      <FormProvider {...methods}>
        <MdmTabs
          tabs={[
            {
              index: 0,
              label: INFORMATION,
              content: <TTProtectionForm disabledFields={disabledFields} />,
            },
            {
              index: 1,
              label: "SUPPLIER / PLANT",
              content: <AffectedSprsForm />,
              disabled: !ttProtection,
            },
          ]}
        />
      </FormProvider>
    </>
  );
};
